import React, { useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '@bumphealth/uxui-component-library';
// The library exposes its stylesheet via a package.json "exports" subpath.
// Webpack 5 resolves it fine; eslint's import resolver doesn't understand
// exports maps, so disable its false positive here.
// eslint-disable-next-line import/no-unresolved
import '@bumphealth/uxui-component-library/styles.css';

import { VisibilityPlaceholder } from '../../../components';
/* Redesign-arm copies of the plan selector. The shared components stayed on the
   legacy design so the control homepage and /select-subscription keep it, and
   their styles are scoped to .homeV2 — both sets define .subContainer. */
import SelectSubscriptionBoxes from '../../../components/select-subscription-boxes-v2';
import TopReviews from '../../product/SubPdp/TopReviews';
import Reveal from './animations';

import hero from './assets/hero.jpg';
// 2.4:1 so the photo fills the width further before the hero's cream gutters open up.
import heroWide from './assets/hero-wide.webp';
import introBg from './assets/intro.jpg';
import giftBabyShower from './assets/gift-babyshower.jpg';
import promoDme from './assets/promo_dme.jpg';
import promoBitsy from './assets/promo_bitsy.jpg';
import shopAll from './assets/shop-all.jpg';
// Award badges reused from the SubPdp final-proof splash.
import award1 from '../../product/SubPdp/assets/award_1.png';
import award2 from '../../product/SubPdp/assets/award_2.png';
import award3 from '../../product/SubPdp/assets/award_3.png';
// Reused "How It Works" step illustrations from the legacy component (bump).
import stepImg1 from '../../../images/bump/how-it-works/how-it-works_step1.svg';
import stepImg2 from '../../../images/bump/how-it-works/how-it-works_step2.svg';
import stepImg3 from '../../../images/bump/how-it-works/how-it-works_step3.svg';

import './style.scss';

// Star / Stars for the social-proof rating (ported from SubPdp).
const Star = () => (
  <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
    <path d='M12 2l3 7 7 .6-5.3 4.7L18 22l-6-3.7L6 22l1.3-7.7L2 9.6 9 9z' />
  </svg>
);

const Stars = ({ count = 5 }) => (
  <span className='stars' aria-label={`${count} stars`}>
    {Array.from({ length: count }).map((_, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <Star key={i} />
    ))}
  </span>
);

// How-It-Works steps — order, illustrations, and copy matched to the legacy
// HowItWorks component.
const STEPS = [
  {
    n: 1,
    img: stepImg1,
    title: 'Pick Your Plan',
    desc: 'Find your best match, skip or cancel anytime.',
  },
  {
    n: 2,
    img: stepImg2,
    title: 'Choose Your FREE Gift',
    desc: 'Add a free welcome gift to your first box.',
  },
  {
    n: 3,
    img: stepImg3,
    title: 'Enjoy Your Bump Box® Every Month',
    desc: 'Filled with products handpicked for your stage of pregnancy.',
  },
];

/**
 * The How It Works band edge: the boundary between the navy panel and the beige
 * band closing the section, with the step discs sitting on it.
 *
 * A parabola apexed at the centre, in a 100x100 viewBox with preserveAspectRatio
 * none so it stretches to any width — distorting a curve is fine. The bumps that
 * detour around each disc do have to stay round, so those are CSS circles on top.
 */
const STEP_DOME = (() => {
  // 0 at the apex, 100 at the box's bottom corners.
  const yAt = (x) => ((x - 50) / 50) ** 2 * 100;

  const SEGMENTS = 48;
  let line = `M 0 ${yAt(0).toFixed(2)}`;
  for (let i = 1; i <= SEGMENTS; i += 1) {
    const x = (i / SEGMENTS) * 100;
    line += ` L ${x.toFixed(2)} ${yAt(x).toFixed(2)}`;
  }

  // Edge height at each step as a fraction of the dome's depth (1 at the apex),
  // off the same parabola as the path so the discs can't drift off the line. x is
  // the cell centre of an equal grid.
  const edgeLift = STEPS.map(
    (_, i) => 1 - yAt(((i + 0.5) / STEPS.length) * 100) / 100,
  );

  return {
    // Runs far below the box: the dome is only as deep as the curve, but the fill
    // has to cover the whole band. No stroke — the edge is the colour boundary.
    fill: `${line} L 100 600 L 0 600 Z`,
    edgeLift,
  };
})();

// Confetti "rivers" leading the eye between sections. x/y are percentages of the
// divider (so a trail holds its shape at any width), s is px.
//
// Gifting → DME: out of the "Shop Pregnancy Essentials" CTA, down-left into the
// pale circles behind the DME copy. Sizes span 4–64px and sit off the centre
// line — an even row of same-size dots reads as a border, not as spill. y runs
// -6%→118%, past both ends, so the trail crosses into both sections (the divider
// is z-index 6, so those dots paint above them).
const RIVER_TO_DME = [
  { x: 58, y: 10, s: 34, tone: 'wash' },
  { x: 49, y: 22, s: 9, tone: 'pink' },
  { x: 44, y: 47, s: 64, tone: 'wash' },
  { x: 53, y: 66, s: 4, tone: 'pale' },
  // Tail pulls right to land under the DME cluster's upper-right circle.
  { x: 43, y: 94, s: 46, tone: 'wash' },
  { x: 34, y: 108, s: 6, tone: 'pink' },
  { x: 48, y: 118, s: 28, tone: 'pale' },
];

// Plans → Social proof: out from under the plan cards into the pink splash. Cream
// tones once it lands on that pink, where a pink wash is invisible.
const RIVER_TO_SOCIAL = [
  { x: 52, y: 6, s: 30, tone: 'wash' },
  { x: 61, y: 18, s: 7, tone: 'pink' },
  { x: 66, y: 42, s: 58, tone: 'wash' },
  { x: 57, y: 64, s: 5, tone: 'pale' },
  { x: 79, y: 106, s: 6, tone: 'cream' },
  { x: 71, y: 118, s: 24, tone: 'cream-wash' },
];

// Social proof → Intro: out of the splash, back across to the left. Cream over the
// pink, pink once it crosses the cream wave.
const RIVER_TO_INTRO = [
  { x: 44, y: 4, s: 26, tone: 'cream-wash' },
  { x: 36, y: 18, s: 6, tone: 'cream' },
  { x: 32, y: 44, s: 52, tone: 'pale' },
  { x: 41, y: 66, s: 5, tone: 'pink' },
  { x: 27, y: 92, s: 34, tone: 'pale' },
  { x: 19, y: 108, s: 7, tone: 'pink' },
  { x: 30, y: 118, s: 22, tone: 'wash' },
];

// Intro → Gifting: off the left of the Intro copy and back to centre. Cream both
// ends, so it stays on the pink ramp with one warm blob for variety.
const RIVER_TO_GIFT = [
  { x: 24, y: 6, s: 28, tone: 'teal-wash' },
  { x: 33, y: 20, s: 6, tone: 'pink' },
  { x: 38, y: 46, s: 54, tone: 'yellow-wash' },
  { x: 30, y: 66, s: 5, tone: 'pale' },
  { x: 44, y: 92, s: 38, tone: 'wash' },
  { x: 53, y: 108, s: 7, tone: 'teal' },
  { x: 45, y: 118, s: 24, tone: 'yellow-wash' },
];

// DME → Bitsy: bottom-left of the DME copy up to top centre, where Bitsy's dashed
// thread picks it up. Shifts to turquoise so the sister palette arrives first.
// The last few sit past 100% on the navy, hence the light tint — mid teal vanishes
// against it.
const RIVER_TO_BITSY = [
  { x: 12, y: 8, s: 44, tone: 'teal-wash' },
  { x: 22, y: 4, s: 5, tone: 'teal' },
  { x: 16, y: 30, s: 8, tone: 'yellow' },
  { x: 29, y: 20, s: 4, tone: 'teal' },
  { x: 33, y: 44, s: 60, tone: 'teal-wash' },
  { x: 23, y: 58, s: 6, tone: 'yellow' },
  { x: 41, y: 36, s: 4, tone: 'teal-wash' },
  { x: 44, y: 68, s: 30, tone: 'yellow-wash' },
  { x: 36, y: 84, s: 7, tone: 'teal-soft' },
  { x: 52, y: 76, s: 11, tone: 'yellow-wash' },
  { x: 46, y: 100, s: 6, tone: 'teal-soft' },
  { x: 50, y: 114, s: 4, tone: 'yellow-wash' },
];

// Navy wave forming the top edge of the Bitsy band. Q then T twice so the
// crest/trough alternation is even, then closed along the bottom.
//
// Closes at y 76, past the viewBox's own 60, so the fill overshoots the SVG's
// bottom edge — closing on 60 put it on a fractional pixel and the antialiased
// row let cream through as a hairline. Flat fill on purpose: a drop-shadow filter
// promoted the SVG to its own layer and seamed, and fake shadows looked worse.
const NAVY_WAVE = 'M0 30 Q 200 -6 400 30 T 800 30 T 1200 30 L1200 76 L0 76 Z';

// Wave edges for the other rivers, same 1200x60 space and overshooting close. Sine
// sampled, so each seam can carry its own amplitude, wavelength and phase.
const waveEdge = ({ amp, cycles, phase, base = 30 }) => {
  const SEGMENTS = 96;
  const yAt = (x) =>
    base + amp * Math.sin((x / 1200) * Math.PI * 2 * cycles + phase);

  let d = `M0 ${yAt(0).toFixed(2)}`;
  for (let i = 1; i <= SEGMENTS; i += 1) {
    const x = (i / SEGMENTS) * 1200;
    d += ` L${x.toFixed(2)} ${yAt(x).toFixed(2)}`;
  }
  return `${d} L1200 76 L0 76 Z`;
};

// Shallower and longer than the Bitsy wave, cresting right of centre.
const SOCIAL_WAVE = waveEdge({ amp: 15, cycles: 1.15, phase: 2.6, base: 32 });

/* These two are the pink band's top and bottom edges, so its thickness is the gap
   between them — cycles and phase must match SOCIAL_WAVE or the band goes lopsided.
   Amplitude is the one free parameter; deeper here, so this is the curvier edge. */
const INTRO_WAVE = waveEdge({ amp: 21, cycles: 1.15, phase: 2.6, base: 30 });

/* The Shop all section's top edge: navy carried down from the Bitsy band, its underside
   a soft wave. An overlay in the neighbouring section's colour rather than a clip, so it
   scales with the viewport (the Bitsy wave does the same). Stretched via
   preserveAspectRatio none; one path serves both breakpoints, since a wave stays soft at
   any width. */
const SHOP_EDGE_TOP = (() => {
  const SEGMENTS = 64;
  const BASE = 50; // mid-height of the wave
  const AMP = 22;
  const CYCLES = 1.4; // barely over one full wave across the width, so it reads calm
  const PHASE = 0.5; // offsets the crest from the left edge
  const yAt = (x) =>
    BASE + AMP * Math.sin((x / 100) * Math.PI * 2 * CYCLES + PHASE);

  let d = `M 0 ${yAt(0).toFixed(2)}`;
  for (let i = 1; i <= SEGMENTS; i += 1) {
    const x = (i / SEGMENTS) * 100;
    d += ` L ${x.toFixed(2)} ${yAt(x).toFixed(2)}`;
  }

  /* Closes past the viewBox's own 0 so the fill overshoots the SVG's top edge. Closing
     on 0 makes that row a path edge — it antialiases to partial alpha and lets a
     hairline through, visible or not depending on where the section lands on the pixel
     grid. The Bitsy wave overshoots its bottom edge for the same reason. */
  return `${d} L 100 -30 L 0 -30 Z`;
})();

// `wave` is the path that forms the next section's top edge, or null for a river
// that just carries dots across a seam.
const ConfettiRiver = ({ dots, variant, twinkle = false, wave = null }) => {
  // Latched, so the loop doesn't restart every time it re-enters the viewport.
  const [started, setStarted] = useState(false);

  const river = (ref) => (
    <div
      ref={ref}
      className={`homeV2-divider homeV2-divider--river homeV2-divider--${variant}${
        twinkle && started ? ' is-twinkling' : ''
      }`}
      aria-hidden='true'
    >
      {wave ? (
        <svg
          className='homeV2-divider__wave'
          viewBox='0 0 1200 60'
          preserveAspectRatio='none'
        >
          <path d={wave} />
        </svg>
      ) : null}
      {dots.map((d, i) => (
        <span
          key={`${d.x}-${d.y}-${d.s}`}
          className={`homeV2-divider__dot homeV2-divider__dot--${d.tone}`}
          style={{
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.s,
            height: d.s,
            // Stagger is the point: the pulse chases down the path, so the
            // trail reads as directional without anything moving.
            ...(twinkle
              ? { animationDelay: `${(i * 0.28).toFixed(2)}s` }
              : null),
          }}
        />
      ))}
    </div>
  );

  if (!twinkle) return river(undefined);

  return (
    <InView
      threshold={0}
      onChange={(inView) => {
        if (inView) setStarted(true);
      }}
    >
      {({ ref }) => river(ref)}
    </InView>
  );
};

// Gifting arc lineup. Order alternates colour with the flagship open pink box in
// the centre, since the middle card is the focal point of the curve.
/**
 * HomeV2 — redesigned bump homepage (bump store only).
 *
 * Presentational shell. All cart/checkout logic stays in the `Home` controller
 * (`src/containers/home/index.js`) and is passed in as handler props, so the
 * add-to-cart flow, modals, and routing behave identically to the legacy home.
 *
 * Order: Hero → How It Works → Plans → Social proof → Intro → Gifting → DME →
 * Bitsy → Shop all → Reviews.
 */
const HomeV2 = (props) => {
  const {
    referralCode,
    modalHeader,
    modalType,
    planFilter,
    matchParams,
    // cart / checkout handlers (owned by the Home controller)
    nextStep,
    handleAddPreCheckoutPromo,
    goToCart,
    updateTitle,
    // navigation handlers
    heroSubscriptionCtaClicked,
    handleSecondHeroButtonClick,
    shopEssentialsClicked,
    claimBreastPumpClicked,
    learnMoreBitsyClicked,
  } = props;

  // Local lazy-reveal state for heavy below-fold sections (presentational only).
  const [showSelectSubs, setShowSelectSubs] = useState(false);

  /* How It Works shows one step at a time below 992px, swiped or arrowed between
     (desktop shows all three, so this only drives the narrow layout). Clamped, not
     wrapping: an arrow only renders where there's somewhere to go. */
  const [activeStep, setActiveStep] = useState(0);
  const swipeStartX = useRef(null);
  const SWIPE_MIN = 40; // px, below this it's a tap or a stray drag

  const stepBy = (delta) =>
    setActiveStep((current) =>
      Math.min(STEPS.length - 1, Math.max(0, current + delta)),
    );

  const onStepsTouchStart = (event) => {
    swipeStartX.current = event.touches[0].clientX;
  };

  const onStepsTouchEnd = (event) => {
    if (swipeStartX.current === null) return;
    const dx = event.changedTouches[0].clientX - swipeStartX.current;
    swipeStartX.current = null;
    if (Math.abs(dx) < SWIPE_MIN) return;
    stepBy(dx < 0 ? 1 : -1);
  };

  const reveal = (isVisible, setter) => {
    if (isVisible) setter(true);
  };

  const scrollToHowItWorks = () => {
    const reducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    document.getElementById('homeV2-howitworks')?.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  // Sticky CTA, revealed once the hero CTA scrolls off the top (as in SubPdp).
  const heroCtaRef = useRef(null);
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        setShowSticky(scrolledPast);
      },
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    // plansV2 scopes the redesigned plan cards — shared with the
    // select-subscription routes, so it isn't tied to the homeV2 class.
    <div className='homeV2 plansV2'>
      {/* ===== 1 · Hero ===== */}
      <section className='homeV2-hero'>
        {/* Wide crop on desktop, square on mobile. The <source> swap keeps the
            LCP attributes on the real <img>. */}
        <picture className='homeV2-hero__picture'>
          <source
            media='(min-width: 992px)'
            srcSet={heroWide}
            type='image/webp'
          />
          <img
            className='homeV2-hero__img'
            src={hero}
            alt=''
            aria-hidden='true'
            // LCP image: eager, high priority. Lowercase `fetchpriority` —
            // React 18 drops the camelCase form; the eslint rule assumes React 19.
            // eslint-disable-next-line react/no-unknown-property
            fetchpriority='high'
            decoding='async'
          />
        </picture>
        <div className='homeV2-hero__scrim' />
        <div className='homeV2-hero__content'>
          <span className='homeV2-eyebrow homeV2-eyebrow--teal'>
            #1 Pregnancy Subscription
          </span>
          <h1 className='homeV2-hero__title'>Celebrate Pregnancy</h1>
          <p className='homeV2-hero__subtitle'>
            Safe &amp; healthy products for expecting moms delivered monthly
          </p>
          {/* The group shrinks to its widest child (the trust line), and the
              button stretches to 100% of it — so the CTA width tracks the trust
              copy below it. */}
          <div className='homeV2-hero__cta-group' ref={heroCtaRef}>
            <Button
              variant='primary'
              size='lg'
              className='homeV2-hero__cta'
              onClick={heroSubscriptionCtaClicked}
            >
              Get Started
              <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
            </Button>
            <p className='homeV2-hero__trust'>
              Free shipping · Cancel anytime · 30-day guarantee
            </p>
          </div>
        </div>
        {/* A button, not an anchor: it scrolls without pushing a hash onto the
            URL. */}
        <button
          type='button'
          className='homeV2-hero__scrollcue'
          aria-label='See how it works'
          onClick={scrollToHowItWorks}
        >
          <FontAwesomeIcon icon={faChevronDown} className='cue-chevron' />
        </button>
      </section>

      {/* One opaque layer from How It Works down, so the sections' overlapping
          seams have a continuous background behind them. */}
      <div className='homeV2-stack'>
        {/* ===== 2 · How it works ===== */}
        <section className='homeV2-howitworks' id='homeV2-howitworks'>
          <div className='homeV2-howitworks__inner'>
            <Reveal className='homeV2-howitworks__head'>
              <span className='homeV2-eyebrow homeV2-eyebrow--pink'>
                How It Works
              </span>
              <h2 className='homeV2-h2'>3 Simple Steps</h2>
            </Reveal>
          </div>

          {/* Desktop spreads all three along the band's edge, which detours around
              each — up for steps 1 and 3, down for 2. Narrow screens show one at a
              time on the apex and swipe between them. */}
          <div
            className='homeV2-steps'
            // Advancing swaps the content with no other cue. Silent on desktop,
            // where all three show and nothing changes.
            aria-live='polite'
            onTouchStart={onStepsTouchStart}
            onTouchEnd={onStepsTouchEnd}
            onTouchCancel={() => {
              swipeStartX.current = null;
            }}
          >
            {/* One stretched dome behind the steps, carrying the beige fill. */}
            <div className='homeV2-steps__band' aria-hidden='true'>
              <svg
                className='homeV2-steps__band-svg'
                viewBox='0 0 100 100'
                preserveAspectRatio='none'
              >
                <path className='homeV2-steps__band-fill' d={STEP_DOME.fill} />
              </svg>
            </div>
            {STEPS.map((step, i) => (
              /* Plain div, not a Reveal: motion sets opacity and transform inline,
                 the two properties the swipe transition needs. Steps park left of
                 the active one and right of it, so the outgoing step leaves to the
                 left and the incoming arrives from the right — and going back
                 mirrors it with no extra state. */
              <div
                key={step.n}
                className={`homeV2-step homeV2-step--${i % 2 ? 'down' : 'up'} ${
                  // eslint-disable-next-line no-nested-ternary
                  i === activeStep
                    ? 'is-active'
                    : i < activeStep
                    ? 'is-before'
                    : 'is-after'
                }`}
                // How high the band's edge runs at this step — see STEP_DOME.
                style={{ '--edge-lift': STEP_DOME.edgeLift[i] }}
              >
                {/* Circle filled in the colour of the side it bulges into, so the
                    edge appears to bend around the disc. */}
                <span className='homeV2-step__bump' aria-hidden='true' />
                <span className='homeV2-step__disc'>
                  <img
                    src={step.img}
                    alt=''
                    aria-hidden='true'
                    className='homeV2-step__img'
                  />
                </span>
                <div className='homeV2-step__label'>
                  {/* "Step" is for screen readers, which would otherwise hear a
                      bare digit. .sr-only is Bootstrap's, loaded globally. */}
                  <span className='homeV2-step__num'>
                    <span className='sr-only'>Step </span>
                    {step.n}
                  </span>
                  <h3 className='homeV2-step__title'>{step.title}</h3>
                  <p className='homeV2-step__desc'>{step.desc}</p>
                </div>
              </div>
            ))}
            {/* The swipe cue, and the tap/keyboard route between steps — swipe
                alone would leave steps 2 and 3 unreachable without touch. Each
                renders only when there's somewhere to go. */}
            {activeStep > 0 ? (
              <button
                type='button'
                className='homeV2-steps__nav homeV2-steps__nav--prev'
                aria-label='Previous step'
                onClick={() => stepBy(-1)}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            ) : null}
            {activeStep < STEPS.length - 1 ? (
              <button
                type='button'
                className='homeV2-steps__nav homeV2-steps__nav--next'
                aria-label='Next step'
                onClick={() => stepBy(1)}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            ) : null}
          </div>
        </section>

        {/* ===== 3 · Plans & pricing (real selector in mock chrome) ===== */}
        <section id='homepageSubscriptionSection' className='homeV2-plans'>
          <div className='homeV2-plans__inner'>
            <Reveal className='homeV2-plans__head'>
              <h2 className='homeV2-h2'>Pick Your Plan</h2>
            </Reveal>

            <InView
              rootMargin='25px 0px 0px 0px'
              threshold={0}
              onChange={(inView) => reveal(inView, setShowSelectSubs)}
            >
              {({ ref }) => (
                <div ref={ref} className='homeV2-plans__selector'>
                  {showSelectSubs ? (
                    /* Separate from the mount trigger above, which fires 25px
                       early to get the cards rendered. This one waits until
                       they're really on screen so the shimmer isn't missed. */
                    <InView threshold={0.35} triggerOnce>
                      {({ ref: shimmerRef, inView: shimmerIn }) => (
                        <div
                          ref={shimmerRef}
                          className={`homeV2-plans__cards${
                            shimmerIn ? ' is-shimmer' : ''
                          }`}
                        >
                          {/* Monthly plans only: prepaid sells from its PDP,
                              so there's no Monthly/Prepaid toggle here. */}
                          <SelectSubscriptionBoxes
                            referralCode={referralCode}
                            showMonthlyPlans
                            nextStep={nextStep}
                            subBoxContainerId='sub-wrapper'
                            handleAddPreCheckoutPromo={
                              handleAddPreCheckoutPromo
                            }
                            updateTitle={updateTitle}
                            modalTitle={modalHeader}
                            modalType={modalType}
                            goToCart={goToCart}
                            planFilter={planFilter}
                            matchParams={matchParams}
                          />
                        </div>
                      )}
                    </InView>
                  ) : (
                    <VisibilityPlaceholder />
                  )}
                </div>
              )}
            </InView>

            <div className='homeV2-plans__trust'>
              <span>Free shipping · Cancel anytime · 30-day guarantee</span>
            </div>
          </div>
        </section>

        <ConfettiRiver
          dots={RIVER_TO_SOCIAL}
          variant='river-social'
          twinkle
          wave={SOCIAL_WAVE}
        />

        {/* ===== 4 · Social proof (As Seen In + awards splash) ===== */}
        <section className='homeV2-social'>
          <div className='final'>
            {/* Reveal the content, not `.final` — sliding the pink band flashes
                cream through its rounded top. */}
            <Reveal className='final-proof'>
              <span className='eyebrow'>Award-winning · Loved by moms</span>
              <div className='score'>
                <span className='big'>4.9</span>
                <div className='score-meta'>
                  <Stars />
                  <span className='trust'>Trusted by 1,000,000+ moms</span>
                </div>
              </div>
              <h2 className='final-title'>
                Voted <span className='accent'>Best</span> Pregnancy
                Subscription,
                <br />
                again and again.
              </h2>
              <Reveal className='awards' delay={0.16}>
                <img
                  src={award1}
                  alt='The Bump — Best Pregnancy Subscription, voted best 2022–2025'
                />
                <img
                  src={award2}
                  alt='What to Expect — Mom Must-Have 2024, Best Pregnancy Subscription'
                />
                <img
                  src={award3}
                  alt="Women's Health — 2024 Best Pregnancy Subscription"
                />
              </Reveal>
              <span className='press'>
                Recommended by Forbes · Healthline · CNET · Marie Claire · Fox
                News
              </span>
            </Reveal>
          </div>
        </section>

        <ConfettiRiver
          dots={RIVER_TO_INTRO}
          variant='river-intro'
          twinkle
          wave={INTRO_WAVE}
        />

        {/* ===== 5 · Transitional intro ===== */}
        <section className='homeV2-intro'>
          <div className='homeV2-intro__inner'>
            <Reveal className='homeV2-intro__media'>
              <img
                src={introBg}
                alt='Expecting mom unpacking her monthly Bump Box'
                loading='lazy'
                decoding='async'
              />
            </Reveal>
            {/* Wrapped, so desktop can set the circle beside the copy as two flex
                children rather than four loose grid items. */}
            <div className='homeV2-intro__copy'>
              <Reveal as='h2' className='homeV2-intro__title'>
                <span className='homeV2-intro__title-a'>Made for Your</span>
                <span className='homeV2-intro__title-b'>Pregnancy Journey</span>
              </Reveal>
              <Reveal as='p' className='homeV2-intro__subtitle'>
                {/* Spans rather than <br>s: they stay inline on mobile, where the
                    explicit {' '} keeps the space, and block on desktop. */}
                <span className='homeV2-intro__line'>
                  Unbox 5–8 pregnancy-safe products tailored to your trimester,
                </span>{' '}
                <span className='homeV2-intro__line'>
                  <span className='homeV2-accent-light'>
                    plus a free welcome gift
                  </span>{' '}
                  in your first box.
                </span>
              </Reveal>
              <Reveal className='homeV2-intro__cta' delay={0.15}>
                <Button
                  variant='primary'
                  size='lg'
                  onClick={heroSubscriptionCtaClicked}
                >
                  GET STARTED
                  <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
                </Button>
              </Reveal>
            </div>
          </div>
        </section>

        <ConfettiRiver dots={RIVER_TO_GIFT} variant='river-gift' twinkle />

        {/* ===== 6 · Gifting (curved card lineup) ===== */}
        <section className='homeV2-gift'>
          <Reveal className='homeV2-gift__head'>
            <div className='homeV2-eyebrow'>
              Celebrate Life&apos;s Sweetest Moments
            </div>
            <h2 className='homeV2-h2'>Gift with Love</h2>
          </Reveal>

          <Reveal className='homeV2-gift__scene'>
            <img
              src={giftBabyShower}
              alt='Friends at a baby shower holding up an "I love Mommy" baby onesie over a table of open Bump Boxes gift boxes, with balloons and an "oh baby" banner behind them'
              loading='lazy'
              decoding='async'
            />
          </Reveal>

          <div className='homeV2-gift__inner'>
            <div className='homeV2-gift__cta'>
              <Button
                variant='primary'
                size='lg'
                className='homeV2-gift__btn'
                onClick={handleSecondHeroButtonClick}
              >
                SHOP GIFT BOXES
                <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
              </Button>
            </div>
          </div>
        </section>

        <ConfettiRiver dots={RIVER_TO_DME} variant='river-dme' twinkle />

        {/* ===== 7 · Breast pump / DME promo =====
            Deliberately off-grid: an asymmetric photo panel with the copy card
            overlapping it (pulled up over the photo on mobile, indented into it
            on desktop) — a different rhythm from the centered bands above. */}
        <section className='homeV2-dme'>
          <div className='homeV2-dme__inner'>
            <Reveal className='homeV2-dme__media'>
              <img
                src={promoDme}
                alt='Mom holding her newborn while using a wearable breast pump'
                loading='lazy'
                decoding='async'
              />
            </Reveal>

            <Reveal className='homeV2-dme__card' delay={0.1}>
              {/* Extra decorative circles (desktop only); the two largest are
                  ::before/::after on the card itself. */}
              <span className='homeV2-dme__circles' aria-hidden='true'>
                <i />
                <i />
              </span>
              <span className='homeV2-eyebrow homeV2-eyebrow--pink'>
                Breast Pumps
              </span>
              <h2 className='homeV2-dme__title'>Support Beyond the Box</h2>
              <p className='homeV2-dme__copy'>
                Get your{' '}
                <span className='homeV2-accent'>FREE or discounted</span> breast
                pump through insurance without the hassle. We take care of
                everything from start to finish.
              </p>
              <Button
                variant='primary'
                size='lg'
                className='homeV2-dme__btn'
                onClick={claimBreastPumpClicked}
              >
                Claim Breast Pump
                <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
              </Button>
            </Reveal>
          </div>
        </section>

        <ConfettiRiver
          dots={RIVER_TO_BITSY}
          variant='river-bitsy'
          twinkle
          wave={NAVY_WAVE}
        />

        {/* ===== 8 · Bitsy cross-sell =====
            Mirrors the DME split (photo left / copy right) on a Bitsy band, so the
            sister brand reads as somewhere else. */}
        <section className='homeV2-bitsy'>
          <span className='homeV2-bitsy__thread' aria-hidden='true'>
            <svg viewBox='0 0 120 64' preserveAspectRatio='none'>
              <path d='M60 0 C60 26 60 26 60 40' />
            </svg>
          </span>

          <div className='homeV2-bitsy__inner'>
            {/* Layered photo: a tilted backing block sits behind the image. */}
            <Reveal className='homeV2-bitsy__media'>
              <img
                src={promoBitsy}
                alt='Mom and baby opening a Bitsy Boxes subscription box together'
                loading='lazy'
                decoding='async'
              />
            </Reveal>

            <Reveal className='homeV2-bitsy__copy' delay={0.1}>
              <span className='homeV2-eyebrow homeV2-bitsy__eyebrow'>
                Next chapter
              </span>
              <h2 className='homeV2-bitsy__title'>
                The Journey Continues with{' '}
                <span className='homeV2-bitsy__brand'>Bitsy Boxes</span>
              </h2>
              <p className='homeV2-bitsy__text'>
                Once baby arrives, enjoy monthly boxes tailored to mom and baby
                with safe essentials, developmental toys, and surprises for
                every stage.
              </p>
              <Button
                variant='white'
                size='lg'
                className='homeV2-bitsy__btn homeV2-cta-lift'
                onClick={learnMoreBitsyClicked}
              >
                Learn More
                <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
              </Button>
            </Reveal>
          </div>
        </section>

        {/* ===== 9 · Shop all =====
            The photo is the section, cut by a wave along the top, with a soft radial
            blast behind the centred copy. The wave overlay is the transition — there is
            no divider element above. */}
        <section className='homeV2-shop'>
          <img
            className='homeV2-shop__bg'
            src={shopAll}
            alt=''
            aria-hidden='true'
            loading='lazy'
            decoding='async'
          />
          {/* Beams over a warm wash, with a centred glow to carry the copy. One
              element, five background layers — see the SCSS. */}
          <span className='homeV2-shop__wash' aria-hidden='true' />

          <svg
            className='homeV2-shop__edge homeV2-shop__edge--top'
            viewBox='0 0 100 100'
            preserveAspectRatio='none'
            aria-hidden='true'
          >
            <path d={SHOP_EDGE_TOP} />
          </svg>

          <div className='homeV2-shop__inner'>
            <Reveal className='homeV2-shop__card'>
              <h2 className='homeV2-shop__title'>
                Everything Mom Needs, All in One Place
              </h2>
              <p className='homeV2-shop__text'>
                Browse pregnancy-safe essentials, postpartum favorites, and baby
                must-haves.
              </p>
              <Button
                variant='primary'
                size='lg'
                className='homeV2-shop__btn'
                onClick={shopEssentialsClicked}
              >
                Shop Pregnancy Essentials
                <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
              </Button>
            </Reveal>
          </div>
        </section>

        {/* ===== 10 · Reviews (reused SubPdp TopReviews; site-wide Yotpo) ===== */}
        <div className='homeV2-reviews'>
          <TopReviews productId='yotpo_site_reviews' />
        </div>
      </div>

      {/* Sticky bottom CTA (mobile). Kept a direct child of .homeV2 so no
          transformed ancestor can become its containing block. */}
      <div
        className={`homeV2-sticky-cta${showSticky ? ' on' : ''}`}
        aria-hidden={!showSticky}
      >
        <div className='homeV2-sticky-cta__copy'>
          <span className='homeV2-sticky-cta__lede'>FREE</span>
          <span className='homeV2-sticky-cta__trust'>welcome gift</span>
        </div>
        <Button
          variant='primary'
          size='lg'
          onClick={heroSubscriptionCtaClicked}
          tabIndex={showSticky ? 0 : -1}
        >
          Get Started
          <FontAwesomeIcon icon={faArrowRight} className='cta-arrow' />
        </Button>
      </div>
    </div>
  );
};

export default HomeV2;
